import { useTranslation } from "react-i18next";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Link,
  Rating,
  Stack,
  Typography,
} from "@mui/material";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import ShopRoundedIcon from "@mui/icons-material/ShopRounded";
import FormatQuoteRoundedIcon from "@mui/icons-material/FormatQuoteRounded";
import PageHero from "../components/common/PageHero";
import Reveal from "../components/motion/Reveal";
import appsContent, { getAppDescription, getAppName, getAppScreenshots, getReviewText } from "../data/appsContent";

function ScreenshotGallery({ app }) {
  const { t, i18n } = useTranslation();
  const appName = getAppName(app, i18n.language);
  const screenshots = getAppScreenshots(app, i18n.language);

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="overline" color="text.secondary">
        {t("screenshots")}
      </Typography>
      <Box
        sx={{
          mt: 1.5,
          display: "flex",
          gap: 2,
          flexWrap: { xs: "nowrap", md: "wrap" },
          overflowX: { xs: "auto", md: "visible" },
          pb: { xs: 1.5, md: 0 },
          scrollSnapType: { xs: "x mandatory", md: "none" },
          // tidy scrollbar on the horizontal mobile rail
          "&::-webkit-scrollbar": { height: 8 },
        }}
      >
        {screenshots.map((shot, i) => (
          <Box
            key={shot}
            component="img"
            src={shot}
            alt={`${appName} — screenshot ${i + 1}`}
            loading="lazy"
            decoding="async"
            sx={{
              // Show each screenshot fully at its natural aspect ratio.
              height: { xs: 280, sm: 340, md: 380 },
              width: "auto",
              maxWidth: "100%",
              objectFit: "contain",
              display: "block",
              flex: "0 0 auto",
              scrollSnapAlign: "start",
              borderRadius: 3,
              bgcolor: "background.default",
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0 18px 40px -22px rgba(13,18,40,0.55)",
              transition: "transform .35s ease, box-shadow .35s ease",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 26px 50px -24px rgba(13,18,40,0.7)",
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

function AppsPage() {
  const { t, i18n } = useTranslation();

  return (
    <Box>
      <PageHero eyebrow={t("productsEyebrow")} title={t("appsTitle")} subtitle={t("appsLead")} />

      <Container maxWidth="lg" sx={{ pt: { xs: 5, md: 7 }, pb: 4 }}>
        <Stack spacing={{ xs: 5, md: 8 }}>
          {appsContent.map((app) => (
            <Reveal key={app.key} direction="up">
              <Card sx={{ overflow: "hidden" }}>
                {/* Header band */}
                <Box
                  sx={{
                    position: "relative",
                    p: { xs: 3, md: 4 },
                    background: "linear-gradient(150deg,#1b2550 0%,#0e1428 100%)",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      opacity: 0.5,
                      background:
                        "radial-gradient(circle at 12% 20%, rgba(79,124,255,0.4), transparent 45%), radial-gradient(circle at 85% 90%, rgba(34,211,238,0.35), transparent 45%)",
                    }}
                  />
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                    sx={{ position: "relative" }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar
                        src={app.iconUrl}
                        alt={getAppName(app, i18n.language)}
                        variant="rounded"
                        sx={{
                          width: 72,
                          height: 72,
                          borderRadius: 3,
                          boxShadow: "0 12px 28px -10px rgba(0,0,0,0.6)",
                        }}
                      />
                      <Box>
                        <Typography variant="h6" sx={{ color: "#fff" }}>
                          {getAppName(app, i18n.language)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", mb: 1 }}>
                          {app.developer}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Rating value={parseFloat(app.rating)} precision={0.1} size="small" readOnly />
                          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
                            {app.rating} / 5
                          </Typography>
                        </Stack>
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip
                        icon={<StarRoundedIcon sx={{ fontSize: 18 }} />}
                        label={`${t("rating")}: ${app.rating}`}
                        sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.3)", bgcolor: "rgba(255,255,255,0.08)" }}
                        variant="outlined"
                      />
                      <Chip
                        icon={<DownloadRoundedIcon sx={{ fontSize: 18 }} />}
                        label={`${t("installs")}: ${app.installs}`}
                        sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.3)", bgcolor: "rgba(255,255,255,0.08)" }}
                        variant="outlined"
                      />
                    </Stack>
                  </Stack>
                </Box>

                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <Typography sx={{ mb: 3 }} color="text.secondary">
                    {getAppDescription(app, i18n.language)}
                  </Typography>

                  {/* Full screenshots gallery */}
                  <ScreenshotGallery app={app} />

                  <Divider sx={{ my: 3 }} />

                  <Typography variant="overline" color="text.secondary">
                    {t("userFeedback")}
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 0.5, mb: 3 }}>
                    {app.reviews.map((review) => (
                      <Grid item xs={12} sm={6} key={review.author}>
                        <Box
                          sx={{
                            height: "100%",
                            p: 2.5,
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: "divider",
                            bgcolor: "background.default",
                            position: "relative",
                          }}
                        >
                          <FormatQuoteRoundedIcon
                            sx={{
                              position: "absolute",
                              top: 14,
                              right: 14,
                              fontSize: 30,
                              color: "primary.main",
                              opacity: 0.25,
                            }}
                          />
                          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                            <Avatar
                              sx={{
                                width: 40,
                                height: 40,
                                fontWeight: 700,
                                fontSize: 16,
                                background: "linear-gradient(135deg,#4f7cff,#22d3ee)",
                                color: "#fff",
                              }}
                            >
                              {review.author.charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                {review.author}
                              </Typography>
                              <Rating value={review.rating} size="small" readOnly />
                            </Box>
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            {getReviewText(review, i18n.language)}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  <Button
                    variant="contained"
                    startIcon={<ShopRoundedIcon />}
                    component={Link}
                    href={app.playUrl}
                    target="_blank"
                    rel="noreferrer"
                    underline="none"
                  >
                    {t("openInPlay")}
                  </Button>
                </CardContent>
              </Card>
            </Reveal>
          ))}
        </Stack>
      </Container>
    </Box>
  );
}

export default AppsPage;
